import React, {useEffect, useState} from 'react';
import { Form } from 'antd';
import './DynamicOrderForm.css';
import FilesSection from "./DynamicOrderForm/common-sections/FilesSection";
import ClientInfoSection from "./DynamicOrderForm/common-sections/ClientInfoSection";
import FinanceSection from "./DynamicOrderForm/common-sections/FinanceSection";
import ApplicantInfoSection from "./DynamicOrderForm/common-sections/ApplicantInfoSection";
import TrademarkSection from "./DynamicOrderForm/basic-sections/TrademarkSection";
import CopyrightSection from "./DynamicOrderForm/basic-sections/CopyrightSection";
import PatentSection from "./DynamicOrderForm/basic-sections/PatentSection";
import CompanySection from "./DynamicOrderForm/basic-sections/CompanySection";
import BarcodeSection from "./DynamicOrderForm/basic-sections/BarcodeSection";
import OtherSection from "./DynamicOrderForm/basic-sections/OtherSection";
import { getOrderPrefix, generateOrderNoWithPrefix } from "../config/order-no-prefix";

interface ExtraTabItem { key: string; label: string; children: React.ReactNode }

interface DynamicOrderFormProps {
  serviceType: string;
  form: any;
  isEdit?: boolean;
  // 可选：在左侧“基本信息”标签后插入额外的 Tab（用于批量场景等）
  extraTabsAfterBasic?: ExtraTabItem | ExtraTabItem[];
  baseCountryMode?: boolean;
  // 在页面模式下启用 Anchor 导航，而非 Tabs
  anchorMode?: boolean;
}

/**
 * 字段权限说明：
 * - admin, manager, finance: 可查看付款相关字段
 * - admin, manager, sales: 可查看价格字段
 * - admin, manager: 可查看成本、销售等敏感字段
 * - 普通用户: 可查看基本业务字段和客户信息
 */

const DynamicOrderForm: React.FC<DynamicOrderFormProps> = ({ serviceType, form, isEdit = false, extraTabsAfterBasic, baseCountryMode = false, anchorMode = false }) => {

  const [isAutoFilledUser, setIsAutoFilledUser] = useState(false);

  // 获取只读样式的helper函数
  const getReadOnlyProps = (fieldType: 'input' | 'select' = 'input') => {
    // 如果是自动填充的用户，或者已关联用户（存在 client_id），设置为只读
    const shouldReadOnly = isAutoFilledUser || !!form.getFieldValue('client_id');

    if (fieldType === 'select') {
      return {
        disabled: shouldReadOnly
      };
    } else {
      return {
        readOnly: shouldReadOnly,
        style: shouldReadOnly ? { backgroundColor: '#f5f5f5', color: '#999' } : {}
      };
    }
  };


  // 监听子业务变化以更新订单编号前缀
  const watchedService = Form.useWatch('service');
  const watchedSubService = Form.useWatch('sub_service');

  // 监听订单编号（用于在被 reset 后重新生成）
  const watchedOrderNo = Form.useWatch('order_no');

  // 自动生成订单编号（初始）
  useEffect(() => {
    if (!isEdit) {
      const prefix = getOrderPrefix(serviceType, form?.getFieldValue?.('sub_service'));
      const orderNo = generateOrderNoWithPrefix(prefix);
      form.setFieldsValue({ order_no: orderNo });
    }
  }, [form, isEdit, serviceType]);

  // 当子业务变更时，使用对应前缀重新生成订单编号
  useEffect(() => {
    if (!isEdit && watchedSubService !== undefined) {
      const prefix = getOrderPrefix(serviceType, watchedSubService);
      const orderNo = generateOrderNoWithPrefix(prefix);
      form.setFieldsValue({ order_no: orderNo });
    }
  }, [watchedSubService, isEdit, form, serviceType]);

  // 若因 resetFields 导致订单编号被清空，则自动补全一个
  useEffect(() => {
    if (!isEdit && (!watchedOrderNo || String(watchedOrderNo).trim() === '')) {
      const prefix = getOrderPrefix(serviceType, form?.getFieldValue?.('sub_service'));
      const orderNo = generateOrderNoWithPrefix(prefix);
      form.setFieldsValue({ order_no: orderNo });
    }
  }, [watchedOrderNo, isEdit, form, serviceType]);

  // 监听 client_id 变化，动态切换只读
  const watchedClientId = Form.useWatch('client_id');
  useEffect(() => {
    setIsAutoFilledUser(!!watchedClientId);
  }, [watchedClientId]);

  // 针对商标业务，使用左侧 Tab 方式展示
  if (serviceType === 'trademark') {
    return (
      <div>
        <section id="section-business">
          <h3>基本信息</h3>
          <TrademarkSection form={form} serviceType={serviceType} getReadOnlyProps={getReadOnlyProps} baseCountryMode={baseCountryMode} />
        </section>
        {watchedSubService !== 'search' && (
          <section id="section-applicant">
            <h3>申请人信息</h3>
            <ApplicantInfoSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
          </section>
        )}
        {extraTabsAfterBasic && (
          Array.isArray(extraTabsAfterBasic) ? (
            extraTabsAfterBasic.map((e) => (
              <section id={`section-${e.key}`} key={e.key}>
                <h3>{e.label}</h3>
                {e.children}
              </section>
            ))
          ) : (
            <section id={`section-${extraTabsAfterBasic.key}`}>
              <h3>{extraTabsAfterBasic.label}</h3>
              {extraTabsAfterBasic.children}
            </section>
          )
        )}
        <section id="section-client">
          <h3>客户信息</h3>
          <ClientInfoSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-price">
          <h3>财务信息</h3>
          <FinanceSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-files">
          <h3>文件</h3>
          <FilesSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
      </div>
    );
  }

  // 针对专利业务，使用左侧 Tab 方式展示（垂直一列布局）
  if (serviceType === 'patent') {
    return (
      <div>
        <section id="section-business">
          <h3>基本信息</h3>
          <PatentSection form={form} serviceType={serviceType} getReadOnlyProps={getReadOnlyProps} baseCountryMode={baseCountryMode} />
        </section>
        <section id="section-applicant">
          <h3>申请人信息</h3>
          <ApplicantInfoSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        {extraTabsAfterBasic && (
          Array.isArray(extraTabsAfterBasic) ? (
            extraTabsAfterBasic.map((e) => (
              <section id={`section-${e.key}`} key={e.key}>
                <h3>{e.label}</h3>
                {e.children}
              </section>
            ))
          ) : (
            <section id={`section-${extraTabsAfterBasic.key}`}>
              <h3>{extraTabsAfterBasic.label}</h3>
              {extraTabsAfterBasic.children}
            </section>
          )
        )}
        <section id="section-client">
          <h3>客户信息</h3>
          <ClientInfoSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-price">
          <h3>财务信息</h3>
          <FinanceSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-files">
          <h3>文件</h3>
          <FilesSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
      </div>
    );
  }

  // 版权业务：左侧 Tab + 垂直布局（Basic=orders；基本信息=orders_copyright）
  if (serviceType === 'copyright') {

    return (
      <div>
        <section id="section-business">
          <h3>基本信息</h3>
          <CopyrightSection serviceType={serviceType} getReadOnlyProps={getReadOnlyProps} baseCountryMode={baseCountryMode} />
        </section>
        <section id="section-applicant">
          <h3>申请人信息</h3>
          <ApplicantInfoSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        {extraTabsAfterBasic && (
          Array.isArray(extraTabsAfterBasic) ? (
            extraTabsAfterBasic.map((e) => (
              <section id={`section-${e.key}`} key={e.key}>
                <h3>{e.label}</h3>
                {e.children}
              </section>
            ))
          ) : (
            <section id={`section-${extraTabsAfterBasic.key}`}>
              <h3>{extraTabsAfterBasic.label}</h3>
              {extraTabsAfterBasic.children}
            </section>
          )
        )}
        <section id="section-client">
          <h3>客户信息</h3>
          <ClientInfoSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-price">
          <h3>财务信息</h3>
          <FinanceSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-files">
          <h3>文件</h3>
          <FilesSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
      </div>
    );
  }

  // 公司业务：左侧 Tab + 垂直布局
  if (serviceType === 'company') {
    return (
      <div>
        <section id="section-business">
          <h3>基本信息</h3>
          <CompanySection serviceType={serviceType} getReadOnlyProps={getReadOnlyProps} baseCountryMode={baseCountryMode} />
        </section>
        {/* 公司业务不需要申请人信息 - 不渲染 Anchor/Section */}
        {extraTabsAfterBasic && (
          Array.isArray(extraTabsAfterBasic) ? (
            extraTabsAfterBasic.map((e) => (
              <section id={`section-${e.key}`} key={e.key}>
                <h3>{e.label}</h3>
                {e.children}
              </section>
            ))
          ) : (
            <section id={`section-${extraTabsAfterBasic.key}`}>
              <h3>{extraTabsAfterBasic.label}</h3>
              {extraTabsAfterBasic.children}
            </section>
          )
        )}
        <section id="section-client">
          <h3>客户信息</h3>
          <ClientInfoSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-price">
          <h3>财务信息</h3>
          <FinanceSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-files">
          <h3>文件</h3>
          <FilesSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
      </div>
    );
  }

  // 条形码业务：左侧 Tab + 垂直布局（Basic=orders；基本信息=orders_barcode）
  if (serviceType === 'barcode') {
    return (
      <div>
        {extraTabsAfterBasic && (
          Array.isArray(extraTabsAfterBasic) ? (
            extraTabsAfterBasic.map((e) => (
              <section id={`section-${e.key}`} key={e.key}>
                <h3>{e.label}</h3>
                {e.children}
              </section>
            ))
          ) : (
            <section id={`section-${extraTabsAfterBasic.key}`}>
              <h3>{extraTabsAfterBasic.label}</h3>
              {extraTabsAfterBasic.children}
            </section>
          )
        )}
        <section id="section-business">
          <h3>基本信息</h3>
          <BarcodeSection serviceType={serviceType} getReadOnlyProps={getReadOnlyProps} baseCountryMode={baseCountryMode} />
        </section>
        <section id="section-client">
          <h3>客户信息</h3>
          <ClientInfoSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-price">
          <h3>财务信息</h3>
          <FinanceSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-files">
          <h3>文件</h3>
          <FilesSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
      </div>
    );
  }

  if (serviceType === 'other') {
    return (
      <div>
        <section id="section-business">
          <h3>基本信息</h3>
          <OtherSection serviceType={serviceType} getReadOnlyProps={getReadOnlyProps} baseCountryMode={baseCountryMode} hideSubService />
        </section>
        {extraTabsAfterBasic && (
          Array.isArray(extraTabsAfterBasic) ? (
            extraTabsAfterBasic.map((e) => (
              <section id={`section-${e.key}`} key={e.key}>
                <h3>{e.label}</h3>
                {e.children}
              </section>
            ))
          ) : (
            <section id={`section-${extraTabsAfterBasic.key}`}>
              <h3>{extraTabsAfterBasic.label}</h3>
              {extraTabsAfterBasic.children}
            </section>
          )
        )}
        <section id="section-client">
          <h3>客户信息</h3>
          <ClientInfoSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-price">
          <h3>财务信息</h3>
          <FinanceSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
        <section id="section-files">
          <h3>文件</h3>
          <FilesSection form={form} isEdit={isEdit} getReadOnlyProps={getReadOnlyProps} />
        </section>
      </div>
    );
  }

  // 未匹配到已知业务类型时不渲染内容
  return null;
};

export default DynamicOrderForm;
